import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import SingleOrderItem from '../../../component/pages/orders/singleOrder/item/item';
import ApiService from '../../../services/apiService';
import Progress from '../../../component/pages/orders/singleOrder/Progress/progress';
import PaySection from '../../../component/pages/orders/singleOrder/paymentSection/paymentSection';
import { OrderType } from '../../../../Enums/config';
import { BiReceipt } from 'react-icons/bi';
import { FiArrowLeft, FiPackage, FiUser, FiFileText, FiCreditCard } from 'react-icons/fi';

import { MdTableRestaurant } from 'react-icons/md';
import './enhancedOrderDetails.css';

const CashOrderDetails = ({ Currency }) => {
  const [orderDetails, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const { state } = useLocation();
  const itemData = state;
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  useEffect(() => {
    // Check if itemData is already an object (the order data)
    if (itemData && typeof itemData === 'object' && itemData.id) {
      // Data is already available, use it directly
      setOrders(itemData);
      setLoading(false);
    } else if (itemData && typeof itemData === 'string') {
      // itemData is a string (order ID), fetch from API
      getItemDetails();
    } else {
      // No data available
      setLoading(false);
    }
  }, [itemData]);

  const getItemDetails = async () => {
    try {
      setLoading(true);
      const apiService = new ApiService();
      const itemNumber = itemData.replace(/^ORD-/i, '');
      let parameter = `?id=${itemNumber}`;
      let res = await apiService.getApiParamater('GET_ORDERS', parameter);
      setOrders(res?.data?.result?.[0] || {});
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getOrderTypeDisplay = (type) => {
    if (type === OrderType.Delivery || type === 1) return { text: 'Delivery', icon: '🚚', className: 'badge-delivery' };
    if (type === OrderType.DineIn || type === 2) return { text: 'Dine In', icon: '🍽️', className: 'badge-dinein' };
    if (type === OrderType.OnlineOrder || type === 3) return { text: 'Online Order', icon: '💻', className: 'badge-online' };
    return { text: 'Takeaway', icon: '🥡', className: 'badge-takeaway' };
  };

  const getPaymentTypeDisplay = (status) => {
    if (status === 1) return { text: 'Cash', icon: '💵', className: 'payment-cash' };
    if (status === 2) return { text: 'Card', icon: '💳', className: 'payment-card' };
    if (status === 3) return { text: 'Stripe', icon: '💻', className: 'payment-stripe' };
    return { text: 'Unknown', icon: '❓', className: 'payment-cash' };
  };

  const getOrderStatusDisplay = (status) => {
    switch (status) {
      case 0:
        return { text: 'New Order', className: 'status-new-order', color: '#6E5199', bg: '#E8E2FF' };
      case 1:
        return { text: 'Accepted', className: 'status-accepted', color: '#28a745', bg: '#d4edda' };
      case 2:
        return { text: 'Preparing', className: 'status-preparing', color: '#856404', bg: '#fff3cd' };
      case 3:
        return { text: 'Ready For Delivery', className: 'status-ready-delivery', color: '#17a2b8', bg: '#d1ecf1' };
      case 4:
        return { text: 'Ready For Pickup', className: 'status-ready-pickup', color: '#17a2b8', bg: '#d1ecf1' };
      case 5:
        return { text: 'Out For Delivery', className: 'status-out-delivery', color: '#fd7e14', bg: '#ffeaa7' };
      case 6:
        return { text: 'Completed', className: 'status-completed', color: '#28a745', bg: '#d4edda' };
      case 7:
        return { text: 'Canceled', className: 'status-canceled', color: '#dc3545', bg: '#f8d7da' };
      case 8:
        return { text: 'On-Hold', className: 'status-on-hold', color: '#6c757d', bg: '#e2e3e5' };
      case 9:
        return { text: 'Refund Initiated', className: 'status-refund-initiated', color: '#fd7e14', bg: '#ffeaa7' };
      case 10:
        return { text: 'Refunded', className: 'status-refunded', color: '#dc3545', bg: '#f8d7da' };
      case 11:
        return { text: 'Payment Partially Refunded', className: 'status-partial-refund', color: '#dc3545', bg: '#f8d7da' };
      case 12:
        return { text: 'Payment Partially Paid', className: 'status-partial-paid', color: '#6E5199', bg: '#E8E2FF' };
      default:
        return { text: 'Unknown', className: 'status-unknown', color: '#6c757d', bg: '#e2e3e5' };
    }
  };

  // Get item status styling
  const getItemStatusStyle = (item) => {
    if (item.refunded) {
      return {
        borderColor: '#dc3545',
        borderWidth: '2px',
        borderStyle: 'solid',
        backgroundColor: '#f8d7da',
        opacity: 0.8,
      };
    }
    if (item.voidItem) {
      return {
        borderColor: '#ffc107',
        borderWidth: '2px',
        borderStyle: 'solid',
        backgroundColor: '#fff3cd',
        opacity: 0.8,
      };
    }
    return {
      borderColor: '#e9ecef',
      borderWidth: '1px',
      borderStyle: 'solid',
      backgroundColor: '#ffffff',
    };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!orderDetails || Object.keys(orderDetails).length === 0) {
    return (
      <div className="enhanced-order-details">
        <div className="enhanced-container">
          <div className="enhanced-card">
            <div className="enhanced-card-body">
              <div className="empty-state">
                <FiPackage className="empty-icon" />
                <p>No order details found</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const orderType = getOrderTypeDisplay(orderDetails.orderType);
  const paymentType = getPaymentTypeDisplay(orderDetails.orderType);
  const orderStatus = getOrderStatusDisplay(orderDetails.orderStatus);
  const totalItems = (orderDetails.orderItems?.length || 0) + (orderDetails.orderCombos?.length || 0) + (orderDetails.orderAddOns?.length || 0);
  debugger;
  // Calculate item counts for status indicators
  const itemCounts = {
    total: orderDetails.orderItems?.length || 0,
    refunded: orderDetails.orderItems?.filter((item) => item.refunded).length || 0,
    void: orderDetails.orderItems?.filter((item) => item.voidItem).length || 0,
    active: orderDetails.orderItems?.filter((item) => !item.refunded && !item.voidItem).length || 0,
  };

  return (
    <div className="enhanced-order-details">
      <div className="enhanced-container">
        {/* Header Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-body">
            <div className="order-header">
              {/* ← Back Button */}
              <div className="w-25">
                <button
                  onClick={goBack}
                  className="btn btn-sm"
                  style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '6px 12px',
                    backgroundColor: '#6E5199',
                    borderColor: '#6E5199',
                    color: '#fff',
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  title="Go back"
                >
                  <FiArrowLeft />
                  Back
                </button>
              </div>

              <div className="order-header-main">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h1 className="order-title">Order Details</h1>
                  <div className="order-meta">
                    <span className="order-number">Order {orderDetails.id}</span>
                    <span className={`status-badge ${orderStatus.className}`}>{orderStatus.text}</span>
                  </div>
                  <div className="order-date">
                    <small>Order Date: {formatDate(orderDetails.orderDate)}</small>
                  </div>
                </div>
                <div className="order-total">
                  <p className="total-label">Total Amount</p>
                  <p className="total-amount">
                    {Currency?.currency?.code}
                    {formatCurrency(orderDetails?.orderAmount || 0)}
                  </p>
                  {orderDetails?.amountRefunded > 0 && (
                    <p className="refunded-amount" style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                      Refunded: {Currency?.currency?.code}
                      {formatCurrency(orderDetails.amountRefunded)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-body">
            <Progress val={orderDetails} />
          </div>
        </div>
        {/* Order Items Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <FiPackage className="section-icon text-blue-600" />
              Order Items
              <div className="item-status-badges" style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <span className="item-count-badge" style={{ backgroundColor: '#6E5199', color: '#fff' }}>
                  Total: {}
                  {itemCounts.total}
                </span>
                {itemCounts.active > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>
                    Active: {itemCounts.active}
                  </span>
                )}
                {itemCounts.refunded > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>
                    Refunded: {itemCounts.refunded}
                  </span>
                )}
                {itemCounts.void > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#ffc107', color: '#212529' }}>
                    Void: {itemCounts.void}
                  </span>
                )}
              </div>
            </h2>
          </div>
          <div className="enhanced-card-body">
            <div className="items-container">
              {/* Order Items */}
              {orderDetails.orderItems &&
                orderDetails.orderItems.map((val, key) => (
                  <div key={key} className="mb-3">
                    <div
                      className="order-item-card"
                      style={{
                        ...getItemStatusStyle(val),
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative',
                        border: '1px solid #e9ecef',
                        backgroundColor: '#fff',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          width: '100%',
                        }}
                      >
                        {/* Left Content - Main Item Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Item Name */}
                          <h4
                            style={{
                              textDecoration: val.refunded || val.voidItem ? 'line-through' : 'none',
                              color: val.refunded ? '#721c24' : val.voidItem ? '#856404' : '#2c3e50',
                              margin: '0 0 8px 0',
                              fontSize: '16px',
                              fontWeight: '600',
                              lineHeight: '1.3',
                              wordBreak: 'break-word',
                            }}
                          >
                            {val.menuItemName || `Item ID: ${val.itemId}`}
                          </h4>

                          {/* Row layout for Qty, Unit, Total */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              flexWrap: 'wrap',
                              marginBottom: val.kitchenInstructions ? '8px' : 0,
                            }}
                          >
                            {/* Quantity */}
                            <span
                              style={{
                                backgroundColor: val.refunded ? '#f5c6cb' : val.voidItem ? '#ffeaa7' : '#e9ecef',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: val.refunded ? '#721c24' : val.voidItem ? '#856404' : '#495057',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Qty: {val.quantity}
                            </span>

                            {/* Unit Price */}
                            <div style={{ fontSize: '14px' }}>
                              <span style={{ color: '#6c757d', marginRight: '6px' }}>Unit: </span>
                              <span
                                style={{
                                  fontWeight: '600',
                                  color: val.refunded ? '#721c24' : val.voidItem ? '#856404' : '#28a745',
                                }}
                              >
                                {Currency?.currency?.code}&nbsp; {formatCurrency(val.menuItemPrice)}
                              </span>
                            </div>

                            {/* Total */}
                            <div style={{ fontSize: '14px' }}>
                              <span style={{ color: '#6c757d', marginRight: '6px' }}>Total:</span>
                              <span
                                style={{
                                  fontWeight: '700',
                                  color: val.refunded ? '#721c24' : val.voidItem ? '#856404' : '#28a745',
                                  fontSize: '15px',
                                }}
                              >
                                {Currency?.currency?.code}&nbsp; {formatCurrency(val.menuItemPrice * val.quantity)}
                              </span>
                            </div>
                          </div>

                          {/* Kitchen Instructions */}
                          {val.kitchenInstructions && (
                            <div
                              style={{
                                marginTop: '8px',
                                backgroundColor: '#f8f9fa',
                                padding: '6px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#495057',
                                fontStyle: 'italic',
                                border: '1px solid #e9ecef',
                              }}
                            >
                              <strong>Instructions:</strong> {val.kitchenInstructions}
                            </div>
                          )}
                        </div>

                        {/* Right Content - Status Badge */}
                        <div style={{ marginLeft: '16px', flexShrink: 0 }}>
                          {val.refunded && (
                            <span
                              style={{
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                padding: '6px 12px',
                                borderRadius: '16px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              🔄 REFUNDED
                            </span>
                          )}
                          {val.voidItem && (
                            <span
                              style={{
                                backgroundColor: '#ffc107',
                                color: '#212529',
                                padding: '6px 12px',
                                borderRadius: '16px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ❌ VOID
                            </span>
                          )}
                          {!val.refunded && !val.voidItem && (
                            <span
                              style={{
                                backgroundColor: '#28a745',
                                color: '#fff',
                                padding: '6px 12px',
                                borderRadius: '16px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ✅ ACTIVE
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Combos */}
              {orderDetails.orderCombos &&
                orderDetails.orderCombos.map((combo, key) => (
                  <div key={key} className="mb-3">
                    <SingleOrderItem val={combo} orderdata={orderDetails} Currency={Currency} isCombo={true} />
                  </div>
                ))}

              {/* Add-ons */}
              {orderDetails.orderAddOns &&
                orderDetails.orderAddOns.map((addOn, key) => (
                  <div key={key} className="mb-3">
                    <SingleOrderItem val={addOn} orderdata={orderDetails} Currency={Currency} isAddOn={true} />
                  </div>
                ))}

              {totalItems === 0 && (
                <div
                  className="empty-state"
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6c757d',
                  }}
                >
                  <FiPackage className="empty-icon" style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>No items found in this order</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Payment Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <FiCreditCard className="section-icon text-green-600" />
              Payment Information
            </h2>
          </div>
          <div className="enhanced-card-body">
            <PaySection val={orderDetails} />
          </div>
        </div>

        {/* Order Summary */}
        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <BiReceipt className="section-icon text-purple-600" />
              Order Summary
            </h2>
          </div>
          <div className="enhanced-card-body">
            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">
                {Currency?.currency?.code}
                {formatCurrency((orderDetails?.orderAmount || 0) + (orderDetails?.discount || 0) - (orderDetails?.calculatedTax || 0))}
              </span>
            </div>

            {orderDetails?.discount > 0 && (
              <div className="summary-row">
                <span className="summary-label">Discount</span>
                <span className="summary-value positive">
                  -{Currency?.currency?.code} &nbsp;
                  {formatCurrency(orderDetails?.discount || 0)}
                </span>
              </div>
            )}

            <div className="summary-row">
              <span className="summary-label">Tax</span>
              <span className="summary-value">
                {Currency?.currency?.code}&nbsp;
                {formatCurrency(orderDetails?.calculatedTax || 0)}
              </span>
            </div>

            {orderDetails?.amountCollected > 0 && (
              <div className="summary-row">
                <span className="summary-label">Amount Collected</span>
                <span className="summary-value positive">
                  {Currency?.currency?.code}&nbsp;
                  {formatCurrency(orderDetails?.amountCollected)}
                </span>
              </div>
            )}

            {orderDetails?.amountReturned > 0 && (
              <div className="summary-row">
                <span className="summary-label">Amount Returned</span>
                <span className="summary-value info">
                  -{Currency?.currency?.code}&nbsp;
                  {formatCurrency(orderDetails?.amountReturned)}
                </span>
              </div>
            )}

            {orderDetails?.amountRefunded > 0 && (
              <div className="summary-row">
                <span className="summary-label">Amount Refunded</span>
                <span className="summary-value negative">
                  -{Currency?.currency?.code} &nbsp;
                  {formatCurrency(orderDetails?.amountRefunded)}
                </span>
              </div>
            )}

            <hr className="summary-divider" />

            <div className="summary-row summary-total">
              <span className="summary-label">Net Total</span>
              <span className="summary-value">
                {Currency?.currency?.code}&nbsp;
                {formatCurrency((orderDetails?.orderAmount || 0) - (orderDetails?.amountRefunded || 0))}
              </span>
            </div>
          </div>
        </div>

        {/* Information Cards Grid */}
        <div className="info-grid">
          {/* Order Information Card */}
          <div className="enhanced-card">
            <div className="enhanced-card-header">
              <h3 className="section-header">
                <FiFileText className="section-icon text-blue-600" />
                Order Information
              </h3>
            </div>
            <div className="enhanced-card-body">
              <div className="info-row">
                <span className="info-label">
                  <FiUser className="info-icon" />
                  Customer Name
                </span>
                <span className="info-value">{orderDetails?.customerName || 'Guest'}</span>
              </div>

              {orderDetails?.refundReason && (
                <div className="info-row">
                  <span className="info-label">Refund Reason</span>
                  <span
                    className="info-value"
                    style={{
                      color: '#dc3545',
                      backgroundColor: '#f8d7da',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontWeight: '500',
                    }}
                  >
                    {orderDetails.refundReason}
                  </span>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">
                  <FiCreditCard className="info-icon" />
                  Payment Type
                </span>
                <div className={`payment-type ${paymentType.className}`}>
                  <span>{paymentType.icon}</span>
                  <span>{paymentType.text}</span>
                </div>
              </div>

              <div className="info-row">
                <span className="info-label">Order No</span>
                <span className="info-value">{orderDetails?.id || '0'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Invoice Number</span>
                <span className="info-value">{orderDetails?.invoiceNumber || 'N/A'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Order Status</span>
                <span className={`status-badge ${orderStatus.className}`}>{orderStatus.text}</span>
              </div>
            </div>
          </div>

          {/* Customer Details Card */}
          <div className="enhanced-card">
            <div className="enhanced-card-header">
              <h3 className="section-header">
                <FiUser className="section-icon text-green-600" />
                Customer Details
              </h3>
            </div>
            <div className="enhanced-card-body">
              <div className="info-row">
                <span className="info-label">
                  <FiUser className="info-icon" />
                  Customer
                </span>
                <span className="info-value">{orderDetails?.customerName || 'Guest'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Customer Type</span>
                <span className="info-value">{orderDetails?.customerType === 0 ? 'Guest' : 'Registered'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Address</span>
                <span className="info-value">{orderDetails?.address1 || '---'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Mobile No</span>
                <span className="info-value">{orderDetails?.mobileNumber || '---'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Email</span>
                <span className="info-value">{orderDetails?.customerEmail || '---'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(CashOrderDetails);
